import BeforeInitBehaviors from '../../behaviors/beforeInitBehaviors';
import { ServicesType } from './ServicesType';
import { bootstrapManager } from '../startup/bootstrapManager';
import { internalLogger } from '../../interface/v1/clientOsLogger';

/**
 * Handles the initialization sequence of commons services
 * Provides a clean separation of initialization logic from module creation
 */
export class ServiceCriticalInitialization {
  /**
   * Initializes all services in the proper order
   * @param services - The services instance to initialize
   */
  static async initializeServices(services: ServicesType): Promise<void> {
    try {
      /**
       *  Phase 4: Critical Services Initialization
       * Core services (localization, navigation, GraphQL, session)
       * */
      bootstrapManager.setCriticalServicesInit();
      await this.initializeCriticalServices(services);

      /** Phase 5: Pre-Configuration being set by Behaviors
       * */
      bootstrapManager.setPreConfiguration();
      await this.runBeforeInitBehaviors();

      /** Phase 6: Service-specific initialization
       *  Secondary services (routing, tenant handler, service worker, etc.)
       * */
      bootstrapManager.setInitialization();
      await this.initializeSecondaryCriticalServices(services);
    } catch (error) {
      console.error('Service initialization failed:', error);
      bootstrapManager.setError(
        error.message,
        'Service initialization sequence failed'
      );
      throw new Error(`Service initialization failed: ${error.message}`);
    }
  }

  /**
   * Initializes critical services that others depend on
   */
  private static async initializeCriticalServices(
    services: ServicesType
  ): Promise<void> {
    /* istanbul ignore next */
    internalLogger?.debug?.d4?.({
      className: 'ServicesCriticalInitialization',
      methodName: 'initializeCriticalServices()',
      message: 'Initializing critical services in strict order...',
      variables: {}
    });

    const {
      localizationService,
      navigationService,
      graphQLService,
      sessionService,
      tenantHandlerService
    } = services;

    try {
      // EventService.init() was initialized in the wrong place.

      // These must be initialized first and in this specific order
      await localizationService.init();

      navigationService.init(); // Navigation depends on Localization

      await graphQLService.init();

      await sessionService.init();

      // the supportSessionService will be used inside of tenantHandlerService but does not have init function, so is ok to init the tenantHandlerService here
      await tenantHandlerService.init(sessionService);
    } catch (error) {
      console.error('Critical services initialization failed:', error);
      throw new Error(
        `Critical services initialization failed: ${error.message}`
      );
    }
  }

  /**
   * Runs before-initialization behaviors
   */
  private static async runBeforeInitBehaviors(): Promise<void> {
    /* istanbul ignore next */
    internalLogger?.debug?.d4?.({
      className: 'ServicesCriticalInitialization',
      methodName: 'runBeforeInitBehaviors()',
      message: 'Running before-initialization behaviors...',
      variables: {}
    });

    try {
      await BeforeInitBehaviors.init();
    } catch (error) {
      console.error('Before-initialization behaviors failed:', error);
      throw new Error(
        `Before-initialization behaviors failed: ${error.message}`
      );
    }
  }

  /**
   * Initializes secondary critical services
   */
  private static async initializeSecondaryCriticalServices(
    services: ServicesType
  ): Promise<void> {
    /* istanbul ignore next */
    internalLogger?.debug?.d4?.({
      className: 'ServicesCriticalInitialization',
      methodName: 'initializeSecondaryCriticalServices()',
      message: 'Initializing secondary critical services...',
      variables: {}
    });

    const {
      webServiceRouting,
      serviceWorkerService,
      featureFlagService,
      loggerService
    } = services;
    /* istanbul ignore next */
    internalLogger?.debug?.d4?.({
      className: 'ServicesCriticalInitialization',
      methodName: 'initializeSecondaryCriticalServices()',
      message: 'Initializing secondary critical services...',
      variables: {}
    });
    try {
      // Initialize remaining critical services in parallel where possible
      webServiceRouting.initialize();
      serviceWorkerService.init();
      featureFlagService.init();
      await loggerService.init();
    } catch (error) {
      console.error(
        'Secondary critical services initialization failed:',
        error
      );
      throw new Error(
        `Secondary critical services initialization failed: ${error.message}`
      );
    }
  }
}
